import { FIRST_PARTY_APPS, RESERVED_FIRST_PARTY_SEGMENTS, firstPartyApp } from "./first-party-apps.js";
import { z } from "zod";
//#region src/mount-path.ts
/**
* The ONE canonical `mountPath` rule for app shell entries (#2146).
*
* A shell entry's `mountPath` is the URL prefix the DP shell mounts a
* third-party app bundle at. Before this module the rule was stated FOUR
* times and no two agreed: the app-manifest JSON Schema (`^/[a-z][a-z0-9/-]*$`),
* the fixture JSON Schema (same pattern, independently written), the CP↔DP
* contract (`z.string().min(1)`), and the release-engine version DTO (bare
* `z.string()`, which accepts the empty string). Every consumer now derives
* from the constants here.
*
* ⛔ THE RULE IS EXPRESSED ENTIRELY IN REGEX, NEVER IN A `.refine()`.
* MEASURED on zod 4.4.3: `z.toJSONSchema()` SILENTLY DROPS a `.refine()` —
* under the default options AND under `{ unrepresentable: 'throw' }`, whose
* whole purpose is to fail loudly. A `.refine()`-based reserved check would
* therefore generate a JSON Schema WEAKER than its own source, which is the
* four-surfaces-disagree defect this module exists to remove, reintroduced by
* its own fix and silent. If a future rule genuinely needs cross-field logic,
* it belongs in a `validateAppXxx` function at the API door (see
* `bundle-parser.ts`), never in a schema anything generates from.
*/
/**
* Segments owned by the shell's own React Router table
* (`cloud/dp/shell/src/App.tsx`). An app mounted here renders INSIDE the
* shell's authenticated chrome on a URL the user reads as first-party — UI
* redressing against the shell's trust context (#2146). React Router sends
* `/admin/reports` to the `/*` splat, so nothing under `AdminLayout` consumes
* it and the third-party bundle mounts and executes there.
*
* `system` is the loader's `/system/` location, where the system surfaces (login, setup, OAuth
* callback and consent, logout, verify-email, maintenance, not-found) are served before any app
* resolves (layered shell surfaces, D18). An app at `/system` would shadow them.
*
* ⚠️ Kept in step with the router by a drift test in the shell package, not by
* hand: two hand-maintained lists diverge. The test asserts CONTAINMENT
* (router segments ⊆ this list), not equality, because the full reserved set
* is deliberately a superset — see {@link RESERVED_GATEWAY_SEGMENTS}.
*/
const RESERVED_ROUTER_SEGMENTS = Object.freeze([
	"auth",
	"change-password",
	"home",
	"invitations",
	"login",
	"logout",
	"oauth",
	"password-reset",
	"settings",
	"system",
	"verify-email",
	"watch"
]);
/**
* Segments the DP gateway routes away from the shell before the shell exists
* (`cloud/dp/charts/zion-data-plane/templates/httproute.yaml`, public `:443`
* listener). Reserved for CORRECTNESS, not security — the failure is a broken
* app, not a trust-context breach.
*
* ⚠️ The reason these matter is that the shadow is ASYMMETRIC, which makes it
* a trap rather than an obvious failure. The switcher tile navigates to
* `mountPath` verbatim via `pushState`, so clicking the tile never touches the
* gateway and the app mounts and renders fine. A refresh, deep link, bookmark
* or shared URL DOES hit the gateway, which routes `/v1` to core-api and
* returns a raw JSON 404. MEASURED against the live DP: `/v1` and `/scim`
* return `application/json` 404s while `/home` and an unknown path both return
* the shell's `text/html`. So the app works for its author and 404s for
* everyone they send the link to.
*
* ⭐ `/v1` is doubly wrong: `/v1/<slug>/…` is already the app API namespace,
* partitioned per app by generated per-app HTTPRoutes. UI lives at `/<slug>`,
* API at `/v1/<slug>` — separate axes on purpose.
*
* ⚠️ `healthz` is an `Exact` match at the gateway, so an app there is shadowed
* at the bare path while `/healthz/anything` still reaches the shell and
* mounts — a PARTIALLY live mount, which is the most confusing state of all.
*
* ⚠️ NOT listed, deliberately: `.well-known` (OAuth/OIDC discovery + jwks) and
* `_apps` (the shell's bundle proxy). Both are already unreachable because `.`
* and `_` are absent from the grammar's character class. They are excluded by
* construction rather than by this list; do not "complete" the list with them,
* because a segment containing `.` or `_` can never satisfy
* {@link MOUNT_PATH_GRAMMAR_PATTERN} in the first place.
*/
const RESERVED_GATEWAY_SEGMENTS = Object.freeze([
	"healthz",
	"otlp",
	"scim",
	"v1"
]);
/**
* Every reserved first segment, sorted so the generated regex — and therefore
* the JSON Schema text derived from it — is byte-stable across builds.
*
* Three groups: the shell's own router segments, the gateway segments, and the
* first segment of every first-party app's mount path
* ({@link RESERVED_FIRST_PARTY_SEGMENTS}, R-71). The third group keeps `admin`
* reserved for every third-party publish after the shell's `/admin` rows left
* its router; the admin app itself is admitted through
* {@link mountPathSchemaForApp}, never through this list.
*/
const RESERVED_MOUNT_SEGMENTS = Object.freeze([
	...RESERVED_ROUTER_SEGMENTS,
	...RESERVED_GATEWAY_SEGMENTS,
	...RESERVED_FIRST_PARTY_SEGMENTS
].sort());
/**
* Upper bound on a `mountPath`, applied BEFORE the regex so the pattern never
* runs against an unbounded string. The longest real mount is 17 characters
* (`/product-strategy`), so this is generous by an order of magnitude and
* exists purely to cap match input.
*/
const MOUNT_PATH_MAX_LENGTH = 256;
/**
* The SHAPE half of the rule: an absolute path of one or more lowercase
* segments.
*
* Tightened relative to the historical `^/[a-z][a-z0-9/-]*$`, which put `/`
* inside the character class and so admitted three ambiguous forms:
*   - a TRAILING SLASH (`/build/`), which left `/build` unmatched while
*     `/build/repos/1` matched — a quiet failure, because the switcher tile
*     routes to `mountPath` verbatim so only hand-typed and in-app links broke.
*   - an EMPTY SEGMENT (`/build//repos`), which defeats a prefix comparison.
*   - the BARE ROOT `/` and the empty string, each of which degrades to a
*     match-everything prefix and reinstates the swallow-every-path behaviour
*     of #2139 from data rather than from a code fallback.
*
* Lowercase-only is load-bearing, not cosmetic: React Router matches
* case-INSENSITIVELY by default, so `/ADMIN/reports` resolves to the real
* admin route. A reserved check that only compared lowercase literals would be
* bypassable by changing case. Rejecting uppercase in the GRAMMAR closes that
* without the reserved clause having to be case-aware at all.
*/
const MOUNT_PATH_GRAMMAR_PATTERN = "^/[a-z][a-z0-9-]*(/[a-z0-9-]+)*$";
/**
* The RESERVED half of the rule, as a positive pattern intended for a JSON
* Schema `not`. Anchored with `(/|$)` so it matches on a path-SEGMENT boundary:
* `/admin` and `/admin/reports` match, but `/admin-tools` does not.
*
* ⛔ The `(/|$)` anchor is the whole correctness argument. A bare
* `startsWith`-style prefix test would reject `/v10`, `/scimitar`,
* `/watchlist`, `/authentic` and `/homepage`, all of which are legitimate app
* names. Those five are pinned as must-ACCEPT controls in the test table.
*
* The SUBTREE is reserved, not just the exact segment, because an app author
* cannot know which sub-paths the shell will claim next. `/admin/reports` is
* unrouted today and becomes a real admin page the day someone adds it.
*/
const RESERVED_SEGMENT_PATTERN = `^/(${RESERVED_MOUNT_SEGMENTS.join("|")})(/|$)`;
/**
* Both halves as ONE pattern, via a negative lookahead. This is the form zod
* uses, and the form that survives `z.toJSONSchema()` losslessly.
*
* ⚠️ The published JSON Schemas use the equivalent `allOf` + `not` encoding
* instead (see {@link mountPathJsonSchema}) so they stay valid for a validator
* without ECMA-262 lookahead support. The drift test proves the two encodings
* agree on every case rather than assuming it.
*
* ReDoS: the alternation is a fixed list of 18 literals defined in this file —
* no user input reaches the pattern — and every quantifier is over a
* single-character class with no nested repetition, so matching is linear.
* Input is additionally capped at {@link MOUNT_PATH_MAX_LENGTH}.
*/
const MOUNT_PATH_PATTERN = `^/(?!(${RESERVED_MOUNT_SEGMENTS.join("|")})(/|$))[a-z][a-z0-9-]*(/[a-z0-9-]+)*$`;
/**
* Marker carried in the `description` of the reserved-segment clause of
* {@link mountPathJsonSchema}, so a consumer that must RELAX that one clause can
* find it without pattern-matching the schema's shape.
*
* ⛔ THE OPERATOR IS THE REASON THIS EXISTS. The reserved-prefix rule is a
* PUBLISH-time invariant: the CP publish door refuses it, which is where #2146
* is actually fixed. The DP operator ALSO validates these schemas, but it is a
* resilient RECONCILER (D22), not a door — and `makeAppManifestAccessor` THROWS
* on a rejected manifest, so a manifest already in the field with a reserved
* mountPath would stop rendering entirely and the app would VANISH from the
* data plane. Refusing to serve an app is strictly worse than serving it on a
* path it should never have been given, and the shell's read side already
* handles the collision safely (#2145: the matcher runs before the built-in
* chrome flags, so the chrome names what is actually mounted).
*
* ⚠️ Relax the RESERVED clause only, never the GRAMMAR clause — a malformed
* path is a different question from a reserved one, and the empty string still
* has to be refused everywhere because it degrades to a match-everything prefix.
*
* This mirrors the operator's existing `D8_ALL_OF_MARKER` treatment of the
* "≥1 of services/workers/jobs" clause. Same stance, same mechanism.
*/
const RESERVED_MOUNT_CLAUSE_MARKER = "#2146 publish-time reserved-prefix clause";
const MOUNT_PATH_MESSAGE = `mountPath must be an absolute lowercase path with at least one segment (e.g. "/my-app"), with no trailing slash, and must not start with a segment reserved by the platform (${RESERVED_MOUNT_SEGMENTS.join(", ")})`;
/**
* The canonical zod schema. Every zod-side `mountPath` in CP, DP and the SDK
* derives from this rather than restating a pattern.
*/
const mountPathSchema = z.string().max(256).regex(new RegExp(MOUNT_PATH_PATTERN), { message: MOUNT_PATH_MESSAGE });
/**
* Recursively freeze a plain object/array tree.
*
* ⛔ `Object.freeze` is SHALLOW. On a nested literal it freezes the outer object
* and leaves every nested member writable, so a frozen-looking canonical schema
* still permits `schema.allOf[0].pattern = '…'` from any consumer — and because
* this module is the single source of truth four surfaces are checked against,
* a mutation here would silently move all of them at once. Freeze the whole
* tree, not the handle to it.
*/
function deepFreeze(value) {
	if (value === null || typeof value !== "object") return value;
	for (const v of Object.values(value)) deepFreeze(v);
	return Object.freeze(value);
}
/**
* The JSON Schema fragment the two published schemas must carry, verbatim.
*
* Exported so the drift test can compare the fragment in
* `app-manifest.schema.json` and `fixture.schema.json` against this object by
* VALUE. A behavioural check alone would pass if a schema drifted to a
* different-but-equivalent encoding; a text check alone would pass if both
* drifted together. The drift test does both.
*
* Deep-frozen, per the note on {@link deepFreeze}.
*/
const mountPathJsonSchema = deepFreeze({
	type: "string",
	maxLength: 256,
	allOf: [{ pattern: MOUNT_PATH_GRAMMAR_PATTERN }, {
		description: RESERVED_MOUNT_CLAUSE_MARKER,
		not: { pattern: RESERVED_SEGMENT_PATTERN }
	}],
	description: "URL path prefix in the shell. Lowercase absolute path, at least one segment, no trailing slash. Must not begin with a platform-reserved segment (#2146)."
});
/**
* True when `segment` is reserved, compared case-INSENSITIVELY.
*
* For callers that inspect a segment directly instead of matching the whole
* path. The case-folding is the point: the grammar already rejects uppercase,
* but a consumer reaching for a bare `RESERVED_MOUNT_SEGMENTS.includes(...)`
* would build a check that `/ADMIN` walks straight past, and React Router
* would still render the real admin route for that URL.
*/
function isReservedMountSegment(segment) {
	return RESERVED_MOUNT_SEGMENTS.includes(segment.toLowerCase());
}
/**
* The first path segment of `mountPath`, lowercased, or `''` when there is
* none. `'/'` and `''` both yield `''` — neither has a segment, which is why
* the grammar rejects both.
*/
function firstMountSegment(mountPath) {
	return (mountPath.split("/")[1] ?? "").toLowerCase();
}
/**
* The per-app form of the rule (R-71). A first-party app is admitted at its one
* listed `mountPath`, which may begin with a reserved segment; every other app
* takes {@link mountPathSchema} unchanged.
*
* The CP publish door keys this by the REGISTERED slug (`appEntity.slug`), never
* by the manifest's own `appId`, which a bundle can set to any string.
*/
function mountPathSchemaForApp(appId) {
	const row = firstPartyApp(appId);
	if (row === void 0) return mountPathSchema;
	return z.literal(row.mountPath);
}
/**
* One frozen fragment per first-party slug, built once at module load from
* {@link FIRST_PARTY_APPS}. `first-party-apps.ts` imports nothing, so the order
* of initialisation is fixed and there is no cycle.
*/
const firstPartyMountFragments = new Map(FIRST_PARTY_APPS.map((row) => [row.appId, deepFreeze({
	type: "string",
	const: row.mountPath,
	description: `The mount path the platform admits for the first-party app "${row.appId}" (R-71). Any other value is refused at publish.`
})]));
/**
* The `app-manifest.schema.json` definitions whose `mountPath` carries the #2146
* reserved clause ({@link RESERVED_MOUNT_CLAUSE_MARKER}): `shellEntry` (a legacy
* `shell[]` row) and `surface` (a `surfaces[]` row) (#3673, admin-redesign-asks C-1).
*
* Who reads it: the three readers that admit a first-party app at its listed
* mount path or relax the clause. They are the CLI (`schemaForApp` in
* `tools/zion-app`), the CP publish door (`compileWithFirstPartyMountPath` in
* release-engine) and the DP operator (`relaxReservedMountPath`). Each one walks
* this list and treats `$defs.<name>.properties.mountPath` for every name, so a
* reader that handles `shellEntry` alone cannot exist beside it.
*
* Invariant: the list equals the set of definitions in
* `cloud/platform-sdk/schemas/app-manifest.schema.json` whose `mountPath` holds
* the reserved clause. `__tests__/mount-path.test.ts` reads the published file
* and reds when a third definition gains the clause or one of these loses it.
* Frozen; no side effect at load.
*/
const RESERVED_MOUNT_PATH_SCHEMA_DEFS = Object.freeze(["shellEntry", "surface"]);
/**
* The JSON Schema form of {@link mountPathSchemaForApp}: the frozen
* `{ type, const }` fragment for a listed app, and {@link mountPathJsonSchema}
* by identity for every other app. The CP door compiles the manifest schema
* with `$defs.<name>.properties.mountPath` replaced by this fragment, for every
* name of {@link RESERVED_MOUNT_PATH_SCHEMA_DEFS}.
*/
function mountPathJsonSchemaForApp(appId) {
	return firstPartyMountFragments.get(appId) ?? mountPathJsonSchema;
}
//#endregion
export { MOUNT_PATH_GRAMMAR_PATTERN, MOUNT_PATH_MAX_LENGTH, MOUNT_PATH_MESSAGE, MOUNT_PATH_PATTERN, RESERVED_GATEWAY_SEGMENTS, RESERVED_MOUNT_CLAUSE_MARKER, RESERVED_MOUNT_PATH_SCHEMA_DEFS, RESERVED_MOUNT_SEGMENTS, RESERVED_ROUTER_SEGMENTS, RESERVED_SEGMENT_PATTERN, firstMountSegment, isReservedMountSegment, mountPathJsonSchema, mountPathJsonSchemaForApp, mountPathSchema, mountPathSchemaForApp };

//# sourceMappingURL=mount-path.js.map